import React from 'react';

import { useWindowDimensions } from 'react-native';

import {
  Box, VStack, useMedia, Heading, Divider, Text,
} from '@gluestack-ui/themed';

import { VideoPlayer as ExpoVideoPlayer } from 'expo-video';
import { VideoPlayerWithProgress } from './components/VideoPlayerWithProgress';
import InteractiveTranscript, { TranscriptData } from './transcript/InteractiveTranscript';

import { VideoPlayerProps } from './core/types';

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ resource }) => {
  const { width, height } = useWindowDimensions();
  const media = useMedia();
  const videoRef = React.useRef<ExpoVideoPlayer>(null);

  const isLandscape = width > height && !media.md;
  const isMobile = !media.md;

  // Get transcript data
  const transcript = React.useMemo(
    () => resource?.metadata?.find((m) => m.type === 'transcript' && m.version.startsWith('paragraph'))?.content,
    [resource],
  );

  // Transcript width: Use modal's actual width, not window width
  // Modal widths: 600px (md) or 900px (lg), minus padding (48px)
  // Mobile: full window width (no padding)
  const transcriptWidth = Math.max(
    50,
    isMobile ? width : media.lg ? 852 : 552,
  );

  // Desktop: Calculate fixed video container height
  // Must be the MINIMUM of aspect ratio height and max allowed height
  // Using maxHeight alone causes layout overlap because aspect ratio box
  // calculates its own height from width, ignoring the parent's maxHeight
  const desktopVideoContainerHeight = React.useMemo(() => {
    if (isMobile) return undefined;
    const modalContentHeight = height - 108; // Modal margin (48) + header (60)
    const maxAllowedHeight = modalContentHeight * 0.55; // Reserve ~45% for transcript
    const aspectRatioHeight = transcriptWidth * (9 / 16); // Height from 16:9 aspect ratio
    return Math.min(maxAllowedHeight, aspectRatioHeight);
  }, [isMobile, height, transcriptWidth]);

  return (
    <VStack flex={1} bg={isMobile ? '$black' : 'transparent'}>
      {/* Video container - fixed height on desktop to prevent layout overlap */}
      {/* Uses calculated height (min of aspect ratio and max allowed) so transcript gets predictable space */}
      {/* position="relative" + overflow="hidden" ensures VideoView can't expand beyond container on web */}
      <Box
        w="100%"
        height={desktopVideoContainerHeight}
        alignSelf="center"
        bg="$black"
        position="relative"
        overflow="hidden"
      >
        {/* Absolute positioning ensures video fills container but can't exceed it */}
        <Box position="absolute" top={0} left={0} right={0} bottom={0}>
          <VideoPlayerWithProgress ref={videoRef} resource={resource} />
        </Box>
      </Box>

      {/* Desktop: Visual separation between video and transcript */}
      {!isMobile && !isLandscape && (
        <Box h={16} />
      )}

      {/* Mobile: YouTube-like title section below video */}
      {isMobile && !isLandscape && (
        <Box px="$4" py="$3" bg="$black">
          <Heading size="sm" color="$textInverted" numberOfLines={2}>
            {resource?.title}
          </Heading>
          {resource?.citation && (
            <Text size="xs" color="$textHint" mt="$1" numberOfLines={1}>
              {resource.citation}
            </Text>
          )}
        </Box>
      )}

      {/* Transcript section - with padding on desktop for readability */}
      {!isLandscape && (
        <>
          {isMobile && (
            <Box px="$4" py="$2" bg="$black">
              <Divider bg="$borderDark800" />
            </Box>
          )}
          <Box
            flex={1}
            minHeight={isMobile ? 200 : 300}
            bg={isMobile ? '$black' : 'transparent'}
            px={isMobile ? 0 : 24}
            pb={isMobile ? 0 : 24}
            overflow="hidden"
          >
            <InteractiveTranscript videoRef={videoRef} transcript={transcript as unknown as TranscriptData} width={transcriptWidth} />
          </Box>
        </>
      )}
    </VStack>
  );
};

export type { VideoPlayerProps } from './core/types';
export default VideoPlayer;
